import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { vmsService } from '../../services/vmsService';
import { userService } from '../../services/userService';
import { RootState } from '../../store';
import { BankDetailsSection } from './components/BankDetailsSection';
import { DocumentsSection } from './components/DocumentsSection';
import { PartnerCommercialSection } from './components/PartnerCommercialSection';
import { useSnackbar } from '../../contexts/SnackbarContext';

export const VendorFormPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [initialData, setInitialData] = useState<any>(location.state?.vendor || null);

  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const [departments, setDepartments] = useState<any[]>([]);
  const [vendorTypes, setVendorTypes] = useState<any[]>([]);
  const [commercialInputTypes, setCommercialInputTypes] = useState<any[]>([]);
  const [docTypes, setDocTypes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const [isNewVendorType, setIsNewVendorType] = useState(false);
  const [newVendorTypeName, setNewVendorTypeName] = useState('');
  const [newVendorTypeDesc, setNewVendorTypeDesc] = useState('');

  const [activeTab, setActiveTab] = useState<'normal' | 'lsp' | 'lender'>('normal');
  const isCorporateFinance = currentUser?.department?.toLowerCase() === 'finance';

  const [isNewCommercialType, setIsNewCommercialType] = useState(false);
  const [newCommercialTypeName, setNewCommercialTypeName] = useState('');
  const [newCommercialTypeDesc, setNewCommercialTypeDesc] = useState('');

  // Enhanced states
  const [vendorDocs, setVendorDocs] = useState<{
    id?: number;
    doc_type_id: string;
    isNewType: boolean;
    newTypeName: string;
    file: File | null;
    fileName?: string;
    currentUrl?: string;
  }[]>([]);

  const [vendorProducts, setVendorProducts] = useState<{
    id?: number;
    product_id: string;
    product_name?: string;
    isNewProduct: boolean;
    newProductName: string;
    description: string;
    quoted_price: string;
    quantity: string;
  }[]>([]);

  const [vendorBanks, setVendorBanks] = useState<{
    id?: number;
    bank_name: string;
    account_number: string;
    ifsc_code: string;
    branch_address: string;
    is_default: boolean;
    status?: string;
  }[]>(id ? [] : [{ bank_name: '', account_number: '', ifsc_code: '', branch_address: '', is_default: true }]);

  const [productsList, setProductsList] = useState<any[]>([]);

  const [parameterValues, setParameterValues] = useState<string[]>(['Fee Agreed', 'Quotation Agreed']);
  const [isNewParameter, setIsNewParameter] = useState(false);
  const [newParameterName, setNewParameterName] = useState('');

  const [serviceTypes, setServiceTypes] = useState<string[]>(['IT Services', 'Consulting', 'Logistics', 'Legal', 'Marketing']);
  const [isNewService, setIsNewService] = useState(false);
  const [newServiceName, setNewServiceName] = useState('');

  const [formData, setFormData] = useState<any>({
    vendor_name: '',
    email: '',
    mobile_number: '',
    gst_number: '',
    pan_number: '',
    address: '',
    contact_person: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    branch_address: '',
    department_id: '',
    vendor_type_id: '',
    status: 'active',
    commercial_parameter: '',
    commercial_input_type_id: '',
    commercial_value: '',
    gst_applicable: true,
    tds_applicable: false,
    permanent_address: '',
    office_address: '',
    vendor_category: 'regular',
    nature_of_service: '',
    udyam_number: '',
    gst_types: ['CGST', 'SGST', 'IGST'],
    state: '',
    payout_on_loan_amount: '',
    base_rate: '',
    base_rate_type: 'inclusive',
    relationship_type: '',
    hurdle_rate_base: '',
    hurdle_rate_cap: '',
    fldg: '',
    processing_fee_percent: '',
    processing_fee_share: '',
    penal_charges_applicable: false,
    penal_charges_amount: '',
    login_fees: '',
    hsn_code: '',
    invoicing_period: '10th of every month'
  });

  useEffect(() => {
    if (id && !location.state?.vendor) {
      vmsService.getVendor(id)
        .then(data => setInitialData(data))
        .catch(err => console.error('Failed to fetch vendor', err));
    }
  }, [id, location.state]);

  useEffect(() => {
    if (isSuperAdmin) {
      userService.getDepartments().then(setDepartments).catch(console.error);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchVendorTypes();
    fetchCommercialInputTypes();
    fetchDocTypes();
    fetchProductsList();
    if (initialData) {
      setFormData({
        vendor_name: initialData.vendor_name || '',
        email: initialData.email || '',
        mobile_number: initialData.mobile_number || '',
        gst_number: initialData.gst_number || '',
        pan_number: initialData.pan_number || '',
        address: initialData.address || '',
        contact_person: initialData.contact_person || '',
        bank_name: initialData.bank_name || '',
        account_number: initialData.account_number || '',
        ifsc_code: initialData.ifsc_code || '',
        branch_address: initialData.branch_address || '',
        department_id: '', // Will be set after departments load
        vendor_type_id: '', // Will be set after vendorTypes load
        status: initialData.status || 'active',
        commercial_parameter: initialData.commercial_parameter || '',
        commercial_input_type_id: '', // Will be set after commercialInputTypes load
        commercial_value: initialData.commercial_value || '',
        gst_applicable: initialData.gst_applicable ?? true,
        tds_applicable: initialData.tds_applicable ?? false,
        permanent_address: initialData.permanent_address || '',
        office_address: initialData.office_address || '',
        vendor_category: initialData.vendor_category || 'regular',
        nature_of_service: initialData.nature_of_service || '',
        udyam_number: initialData.udyam_number || '',
        gst_types: initialData.gst_types || ['CGST', 'SGST', 'IGST'],
        state: initialData.state || '',
        payout_on_loan_amount: initialData.payout_on_loan_amount || '',
        base_rate: initialData.base_rate || '',
        base_rate_type: initialData.base_rate_type || 'inclusive',
        relationship_type: initialData.relationship_type || '',
        hurdle_rate_base: initialData.hurdle_rate_base || '',
        hurdle_rate_cap: initialData.hurdle_rate_cap || '',
        fldg: initialData.fldg || '',
        processing_fee_percent: initialData.processing_fee_percent || '',
        processing_fee_share: initialData.processing_fee_share || '',
        penal_charges_applicable: initialData.penal_charges_applicable ?? false,
        penal_charges_amount: initialData.penal_charges_amount || '',
        login_fees: initialData.login_fees || '',
        hsn_code: initialData.hsn_code || '',
        invoicing_period: initialData.invoicing_period || '10th of every month'
      });

      if (initialData.partner_type === 'lsp' || initialData.partner_type === 'lender') {
        setActiveTab(initialData.partner_type);
      } else {
        setActiveTab('normal');
      }

      // Populate custom parameter value if not in list
      if (initialData.commercial_parameter && !['Fee Agreed', 'Quotation Agreed'].includes(initialData.commercial_parameter)) {
        setParameterValues(prev => [...prev, initialData.commercial_parameter]);
      }

      // Populate custom service type if not in list
      if (initialData.nature_of_service && !['IT Services', 'Consulting', 'Logistics', 'Legal', 'Marketing'].includes(initialData.nature_of_service)) {
        setServiceTypes(prev => [...prev, initialData.nature_of_service]);
      }

      // Fetch existing documents
      vmsService.getVendorDocDetails(initialData.id)
        .then(details => {
          const mapped = details.map((d: any) => ({
            id: d.id,
            doc_type_id: String(d.read_doc_type_id || ''),
            isNewType: false,
            newTypeName: '',
            file: null,
            fileName: d.doc_type,
            currentUrl: 'EXISTING'
          }));
          setVendorDocs(mapped);
        })
        .catch(err => console.error('Failed to load vendor documents', err));

      // Fetch existing products
      vmsService.getVendorProducts(initialData.id)
        .then(products => {
          setVendorProducts(products.map((p: any) => ({
            id: p.id,
            product_id: String(p.product_id),
            product_name: p.product_name,
            isNewProduct: false,
            newProductName: p.product_name || '',
            description: p.description || '',
            quoted_price: String(p.quoted_price),
            quantity: String(p.quantity)
          })));
        })
        .catch(err => console.error('Failed to load products', err));

      // Fetch existing banks
      vmsService.getVendorBanks(initialData.id)
        .then(banks => {
          setVendorBanks(banks.filter((b: any) => b.status === 'active').map((b: any) => ({
            id: b.id,
            bank_name: b.bank_name,
            account_number: b.account_number,
            ifsc_code: b.ifsc_code,
            branch_address: b.branch_address,
            is_default: b.is_default
          })));
        })
        .catch(err => console.error('Failed to load banks', err));
    }
  }, [initialData]);

  useEffect(() => {
    if (initialData && departments.length > 0) {
      const dept = departments.find(d => d.name === initialData.department);
      if (dept) {
        setFormData((prev: any) => ({ ...prev, department_id: String(dept.id) }));
      }
    }
  }, [initialData, departments]);



  useEffect(() => {
    if (initialData && vendorTypes.length > 0) {
      const vtype = vendorTypes.find(vt => vt.name === initialData.vendor_type);
      if (vtype) {
        setFormData((prev: any) => ({ ...prev, vendor_type_id: String(vtype.id) }));
      }
    }
  }, [initialData, vendorTypes]);

  useEffect(() => {
    if (initialData && commercialInputTypes.length > 0) {
      const ctype = commercialInputTypes.find(ct => ct.name === initialData.commercial_input_type);
      if (ctype) {
        setFormData((prev: any) => ({ ...prev, commercial_input_type_id: String(ctype.id) }));
      }
    }
  }, [initialData, commercialInputTypes]);

  const fetchVendorTypes = async () => {
    try {
      const data = await vmsService.getVendorTypes();
      setVendorTypes(data.results || data);
    } catch (err) {
      console.error('Failed to fetch vendor types', err);
    }
  };

  const fetchCommercialInputTypes = async () => {
    try {
      const data = await vmsService.getCommercialInputTypes();
      setCommercialInputTypes(data.results || data);
    } catch (err) {
      console.error('Failed to fetch commercial input types', err);
    }
  };

  const fetchProductsList = async () => {
    try {
      const data = await vmsService.getProducts();
      setProductsList(data.results || data);
    } catch (err) {
      console.error('Failed to fetch products', err);
    }
  };

  const fetchDocTypes = async () => {
    try {
      const data = await vmsService.getDocTypes();
      setDocTypes(data.results || data);
    } catch (err) {
      console.error('Failed to fetch doc types', err);
    }
  };



  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleIfscChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const ifsc = e.target.value.toUpperCase();
    setFormData((prev: any) => ({ ...prev, ifsc_code: ifsc }));
    
    if (ifsc.length === 11) {
      try {
        const response = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
        if (response.ok) {
          const data = await response.json();
          setFormData((prev: any) => ({
            ...prev,
            bank_name: data.BANK || prev.bank_name,
            branch_address: data.ADDRESS || prev.branch_address
          }));
        }
      } catch (err) {
        console.error('Failed to fetch bank details from Razorpay IFSC API', err);
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFormData({ ...formData, [e.target.name]: e.target.files[0] });
    }
  };

  const handleRemoveProduct = async (index: number) => {
    const prod = vendorProducts[index];
    if (prod.id && initialData?.id) {
      if (window.confirm(`Are you sure you want to archive this product?`)) {
        try {
          await vmsService.deleteVendorProduct(initialData.id, prod.id);
          setVendorProducts(prev => prev.filter((_, i) => i !== index));
        } catch (err) {
          alert('Failed to archive product.');
        }
      }
    } else {
      setVendorProducts(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (formData.udyam_number) {
      const udyamRegex = /^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/i;
      if (!udyamRegex.test(formData.udyam_number)) {
        alert("Invalid Udyam Aadhaar Number format. Expected format: UDYAM-XX-00-0000000");
        setIsLoading(false);
        return;
      }
    }

    if (vendorBanks.length === 0) {
      showSnackbar('At least one bank account detail is compulsory.');
      setIsLoading(false);
      return;
    }

    const invalidBanks = vendorBanks.filter(
      bank => !bank.bank_name.trim() || !bank.account_number.trim() || !bank.ifsc_code.trim()
    );
    if (invalidBanks.length > 0) {
      showSnackbar('Please fill in all required bank details (Bank Name, Account Number, and IFSC Code).');
      setIsLoading(false);
      return;
    }

    // Auto-ensure at least one bank is default if bank accounts are present
    if (vendorBanks.length > 0 && !vendorBanks.some(b => b.is_default)) {
      vendorBanks[0].is_default = true;
    }

    try {
      const payload = { ...formData, partner_type: activeTab };
      if (!payload.department_id) {
        delete (payload as any).department_id;
      }

      let vendorId = initialData?.id;

      if (vendorId) {
        await vmsService.updateVendor(vendorId, payload);
      } else {
        const response = await vmsService.createVendor(payload);
        vendorId = response.id;
      }

      let currentDocTypes = docTypes;
      if (currentDocTypes.length === 0) {
        const docTypesData = await vmsService.getDocTypes();
        currentDocTypes = docTypesData.results || docTypesData;
        setDocTypes(currentDocTypes);
      }

      for (const doc of vendorDocs) {
        if (!doc.file && !doc.isNewType && doc.id) {
          continue;
        }

        let finalDocTypeId = doc.doc_type_id;

        if (doc.isNewType && doc.newTypeName.trim()) {
          const newType = await vmsService.createDocType({ name: doc.newTypeName, description: `Vendor ${doc.newTypeName} Document` });
          currentDocTypes = [...currentDocTypes, newType];
          setDocTypes(currentDocTypes);
          finalDocTypeId = String(newType.id);
        } else if (doc.doc_type_id.startsWith('INITIAL_')) {
          const name = doc.doc_type_id.replace('INITIAL_', '');
          let existingType = currentDocTypes.find(dt => dt.name.toLowerCase() === name.toLowerCase());
          if (!existingType) {
            existingType = await vmsService.createDocType({ name, description: `Vendor ${name} Document` });
            currentDocTypes = [...currentDocTypes, existingType];
            setDocTypes(currentDocTypes);
          }
          finalDocTypeId = String(existingType.id);
        }

        if (!finalDocTypeId) continue;

        let docDetailId = doc.id;
        if (!docDetailId) {
          const newDetail = await vmsService.createVendorDocDetail(vendorId, { doc_type_id: Number(finalDocTypeId) });
          docDetailId = newDetail.id;
        }

        if (doc.file) {
          const uploadData = new FormData();
          uploadData.append('file', doc.file);
          await vmsService.uploadVendorDocVersion(vendorId, docDetailId!, uploadData);
        }
      }

      let currentProducts = productsList;
      for (const prod of vendorProducts) {
        if (!prod.quoted_price) continue;
        let finalProductId = prod.product_id;

        if (prod.isNewProduct && prod.newProductName.trim()) {
          const newProd = await vmsService.createProduct({ name: prod.newProductName });
          currentProducts = [...currentProducts, newProd];
          setProductsList(currentProducts);
          finalProductId = String(newProd.id);
        }

        if (!finalProductId) continue;

        const prodPayload = {
          product_id: Number(finalProductId),
          description: prod.description,
          quoted_price: parseFloat(prod.quoted_price) || 0,
          quantity: parseInt(prod.quantity, 10) || 1
        };
        if (prod.id) {
          await vmsService.updateVendorProduct(vendorId, prod.id, prodPayload);
        } else {
          await vmsService.createVendorProduct(vendorId, prodPayload);
        }
      }

      for (const bank of vendorBanks) {
        if (!bank.bank_name.trim() || !bank.account_number.trim()) continue;
        const bankPayload = {
          bank_name: bank.bank_name,
          account_number: bank.account_number,
          ifsc_code: bank.ifsc_code,
          branch_address: bank.branch_address,
          is_default: bank.is_default
        };
        if (bank.id) {
          await vmsService.updateVendorBank(vendorId, bank.id, bankPayload);
        } else {
          await vmsService.createVendorBank(vendorId, bankPayload);
        }
      }

      navigate('/vms');
    } catch (err: any) {
      console.error('Failed to save vendor', err);
      showSnackbar(err.message || 'Failed to save vendor');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={() => navigate('/vms')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dark)', padding: 0, display: 'flex' }} title="Back to Listing">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </button>
          <h1 className="page-title" style={{ margin: 0 }}>{initialData ? "Edit Vendor" : "Add New Vendor"}</h1>
        </div>
        <p className="page-description" style={{ marginTop: '0.5rem' }}>Fill out the form below to {initialData ? "update" : "create"} a vendor.</p>
      </div>

      <div className="panel">
        <div style={{ display: 'flex', gap: '2rem', borderBottom: '1px solid #eee', marginBottom: '1.5rem' }}>
          <button 
            type="button"
            onClick={() => setActiveTab('normal')}
            style={{ 
              background: 'none', border: 'none', padding: '0.75rem 0', fontSize: '1rem', fontWeight: activeTab === 'normal' ? 600 : 400,
              color: activeTab === 'normal' ? 'var(--primary)' : 'var(--text-light)', borderBottom: activeTab === 'normal' ? '2px solid var(--primary)' : '2px solid transparent', cursor: 'pointer'
            }}
          >
            Normal Vendor
          </button>
          {isCorporateFinance && (
            <>
              <button 
                type="button"
                onClick={() => setActiveTab('lsp')}
                style={{ 
                  background: 'none', border: 'none', padding: '0.75rem 0', fontSize: '1rem', fontWeight: activeTab === 'lsp' ? 600 : 400,
                  color: activeTab === 'lsp' ? 'var(--primary)' : 'var(--text-light)', borderBottom: activeTab === 'lsp' ? '2px solid var(--primary)' : '2px solid transparent', cursor: 'pointer'
                }}
              >
                LSP
              </button>
              <button 
                type="button"
                onClick={() => setActiveTab('lender')}
                style={{ 
                  background: 'none', border: 'none', padding: '0.75rem 0', fontSize: '1rem', fontWeight: activeTab === 'lender' ? 600 : 400,
                  color: activeTab === 'lender' ? 'var(--primary)' : 'var(--text-light)', borderBottom: activeTab === 'lender' ? '2px solid var(--primary)' : '2px solid transparent', cursor: 'pointer'
                }}
              >
                Lender Partner
              </button>
            </>
          )}
        </div>
        <form onSubmit={handleSubmit}>
          
          {/* 1. Basic Details */}
          <h3 style={{ borderBottom: '1px solid #eee', paddingBottom: '0.5rem', marginBottom: '1.5rem', color: 'var(--text-main)' }}>1. Basic Details</h3>
          
          <div className="form-row">
            <div className="filter-group">
              <label>Vendor Name *</label>
              <input type="text" name="vendor_name" value={formData.vendor_name} onChange={handleChange} placeholder="e.g. Acme Corp" required />
            </div>
            <div className="filter-group">
              <label>Department Association {isSuperAdmin ? '*' : ''}</label>
              {isSuperAdmin ? (
                <select name="department_id" value={formData.department_id} onChange={handleChange} required>
                  <option value="">Select Department</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={currentUser?.department || ''}
                  disabled
                  className="field-disabled"
                />
              )}
            </div>
          </div>
          
          <div className="form-row">
            <div className="filter-group">
              <label>Vendor Type</label>
              {!isNewVendorType ? (
                <select name="vendor_type_id" value={formData.vendor_type_id} onChange={e => {
                  if (e.target.value === 'NEW') setIsNewVendorType(true);
                  else handleChange(e);
                }}>
                  <option value="">Select Type...</option>
                  {vendorTypes.map(vt => (
                    <option key={vt.id} value={vt.id}>{vt.name}</option>
                  ))}
                  <option value="NEW">+ Create New Type</option>
                </select>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                  <input type="text" placeholder="New Type Name" required value={newVendorTypeName} onChange={e => setNewVendorTypeName(e.target.value)} />
                  <input type="text" placeholder="Description (optional)" value={newVendorTypeDesc} onChange={e => setNewVendorTypeDesc(e.target.value)} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                    <button type="button" onClick={() => setIsNewVendorType(false)} style={{ fontSize: '0.85rem', color: 'var(--text-light)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Cancel</button>
                    <button type="button" onClick={async (e) => {
                      e.preventDefault();
                      if (!newVendorTypeName.trim()) return;
                      try {
                        const newType = await vmsService.createVendorType({ name: newVendorTypeName, description: newVendorTypeDesc });
                        const data = await vmsService.getVendorTypes();
                        setVendorTypes(data.results || data);
                        setFormData((prev: any) => ({...prev, vendor_type_id: String(newType.id)}));
                        setIsNewVendorType(false);
                        setNewVendorTypeName('');
                        setNewVendorTypeDesc('');
                      } catch(err) {
                        alert('Failed to save new vendor type');
                      }
                    }} style={{ fontSize: '0.85rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>Save</button>
                  </div>
                </div>
              )}
            </div>
            <div className="filter-group">
              <label>Vendor Category</label>
              <select name="vendor_category" value={formData.vendor_category} onChange={handleChange}>
                <option value="regular">Regular</option>
                <option value="one_time">One-time</option>
                <option value="retainer">Retainer</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="filter-group">
              <label>Contact Person *</label>
              <input type="text" name="contact_person" value={formData.contact_person} onChange={handleChange} placeholder="Full name" required />
            </div>
            <div className="filter-group">
              <label>Email Address *</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="contact@vendor.com" required />
            </div>
          </div>

          <div className="form-row">
            <div className="filter-group">
              <label>Phone Number *</label>
              <input
                  type="tel"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={e => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData({ ...formData, mobile_number: digits });
                  }}
                  placeholder="10-digit mobile number"
                  pattern="\d{10}"
                  title="Enter a valid 10-digit mobile number"
                  required
                />
            </div>
            <div className="filter-group">
              <label>Nature of Service</label>
              {!isNewService ? (
                <select 
                  name="nature_of_service" 
                  value={formData.nature_of_service} 
                  onChange={e => {
                    if (e.target.value === 'NEW') {
                      setIsNewService(true);
                    } else {
                      handleChange(e);
                    }
                  }}
                >
                  <option value="">Select Service Type...</option>
                  {serviceTypes.map(st => (
                    <option key={st} value={st}>{st}</option>
                  ))}
                  <option value="NEW">+ Create New Service Type</option>
                </select>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                  <input 
                    type="text" 
                    placeholder="New Service Type" 
                    required 
                    value={newServiceName} 
                    onChange={e => {
                      setNewServiceName(e.target.value);
                      setFormData((prev: any) => ({ ...prev, nature_of_service: e.target.value }));
                    }} 
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                    <button 
                      type="button" 
                      onClick={() => {
                        setIsNewService(false);
                        setNewServiceName('');
                        setFormData((prev: any) => ({ ...prev, nature_of_service: initialData?.nature_of_service || '' }));
                      }} 
                      style={{ fontSize: '0.85rem', color: 'var(--text-light)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!newServiceName.trim()) return;
                        if (!serviceTypes.includes(newServiceName)) {
                          setServiceTypes(prev => [...prev, newServiceName]);
                        }
                        setIsNewService(false);
                      }}
                      style={{ fontSize: '0.85rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="filter-group">
              <label>Udyam Aadhaar Number</label>
              <input 
                type="text" 
                name="udyam_number" 
                value={formData.udyam_number} 
                onChange={handleChange} 
                placeholder="e.g. UDYAM-MH-12-0012345" 
              />
            </div>
            <div className="filter-group" style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', height: '100%', paddingTop: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', margin: 0 }}>
                <input type="checkbox" name="gst_applicable" checked={formData.gst_applicable} onChange={handleChange} style={{ width: 'auto' }} />
                GST Applicable
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', margin: 0 }}>
                <input type="checkbox" name="tds_applicable" checked={formData.tds_applicable} onChange={handleChange} style={{ width: 'auto' }} />
                TDS Applicable
              </label>
            </div>
          </div>

          {formData.gst_applicable && (
            <div className="form-row" style={{ backgroundColor: '#f9fafb', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '1.5rem', display: 'flex', gap: '2rem', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-dark)' }}>Select Applicable GST Type(s):</span>
              {['CGST', 'SGST', 'IGST'].map(type => (
                <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', margin: 0, fontWeight: 500 }}>
                  <input 
                    type="checkbox" 
                    checked={formData.gst_types?.includes(type)} 
                    onChange={e => {
                      const current = formData.gst_types || [];
                      let updated: string[];
                      if (e.target.checked) {
                        updated = [...current, type];
                      } else {
                        updated = current.filter((t: string) => t !== type);
                      }
                      setFormData((prev: any) => ({ ...prev, gst_types: updated }));
                    }} 
                    style={{ width: 'auto', margin: 0 }}
                  />
                  {type}
                </label>
              ))}
            </div>
          )}

          <div className="form-row">
            <div className="filter-group">
              <label>GST Number</label>
              <input type="text" name="gst_number" value={formData.gst_number} onChange={handleChange} placeholder="Optional" />
            </div>
            <div className="filter-group">
              <label>PAN Number</label>
              <input type="text" name="pan_number" value={formData.pan_number} onChange={handleChange} placeholder="Optional" />
            </div>
          </div>

          <div className="form-row">
            <div className="filter-group form-full" style={{ gridColumn: '1 / -1' }}>
              <label>Permanent Address</label>
              <input type="text" name="permanent_address" value={formData.permanent_address} onChange={handleChange} placeholder="Permanent Address" style={{ width: '100%' }} />
            </div>
          </div>
          <div className="form-row">
            <div className="filter-group form-full" style={{ gridColumn: '1 / -1' }}>
              <label>Office Address</label>
              <input type="text" name="office_address" value={formData.office_address} onChange={handleChange} placeholder="Office Address" style={{ width: '100%' }} />
            </div>
          </div>
          <div className="form-row">
            <div className="filter-group">
              <label>State</label>
              <select name="state" value={formData.state} onChange={handleChange}>
                <option value="">Select State</option>
                {[
                  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", 
                  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", 
                  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", 
                  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", 
                  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", 
                  "Uttar Pradesh", "Uttarakhand", "West Bengal", "Delhi", "Jammu and Kashmir"
                ].map(st => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>
          </div>

          {/* Partner Commercial Details (LSP & Lender only) */}
          <PartnerCommercialSection 
            activeTab={activeTab} 
            formData={formData} 
            handleChange={handleChange} 
            setFormData={setFormData}
            initialData={initialData}
          />

          {/* 2. Documents/Attachments */}
          <DocumentsSection 
            vendorDocs={vendorDocs}
            setVendorDocs={setVendorDocs}
            docTypes={docTypes}
            setDocTypes={setDocTypes}
            initialDataId={initialData?.id}
          />

          {/* 3. Bank Details */}
          <BankDetailsSection 
            vendorBanks={vendorBanks}
            setVendorBanks={setVendorBanks}
            initialDataId={initialData?.id}
          />

          {/* 4. Commercials/Quotation */}
          <>
            <h3 style={{ borderBottom: '1px solid #eee', paddingBottom: '0.5rem', marginBottom: '1.5rem', marginTop: '2.5rem', color: 'var(--text-main)' }}>4. Commercials/Quotation</h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
            {vendorProducts.map((prod, idx) => (
              <div key={idx} className="form-row" style={{ alignItems: 'flex-start', border: '1px solid #eee', padding: '1rem', borderRadius: '8px', backgroundColor: '#fafafa', position: 'relative', flexWrap: 'wrap' }}>
                <div className="filter-group" style={{ flex: '1 1 200px' }}>
                  <label>Product Name *</label>
                  {!prod.isNewProduct ? (
                    <select 
                      value={prod.product_id} 
                      onChange={e => {
                        const updated = [...vendorProducts];
                        if (e.target.value === 'NEW') {
                          updated[idx].isNewProduct = true;
                          updated[idx].newProductName = '';
                          updated[idx].product_id = '';
                        } else {
                          updated[idx].product_id = e.target.value;
                        }
                        setVendorProducts(updated);
                      }}
                      required
                    >
                      <option value="">Select Product...</option>
                      {productsList.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                      <option value="NEW">+ Create New Product</option>
                    </select>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                      <input 
                        type="text" 
                        list={`products-list-${idx}`}
                        placeholder="New Product Name" 
                        required 
                        value={prod.newProductName} 
                        onChange={e => {
                          const updated = [...vendorProducts];
                          updated[idx].newProductName = e.target.value;
                          setVendorProducts(updated);
                        }} 
                      />
                      <datalist id={`products-list-${idx}`}>
                        {productsList.map(p => (
                          <option key={p.id} value={p.name} />
                        ))}
                      </datalist>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                        <button 
                          type="button" 
                          onClick={() => {
                            const updated = [...vendorProducts];
                            updated[idx].isNewProduct = false;
                            updated[idx].newProductName = '';
                            setVendorProducts(updated);
                          }} 
                          style={{ fontSize: '0.85rem', color: 'var(--text-light)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          Cancel
                        </button>
                        <button 
                          type="button" 
                          onClick={async (e) => {
                            e.preventDefault();
                            if (!prod.newProductName.trim()) return;
                            
                            const existing = productsList.find(p => p.name.toLowerCase() === prod.newProductName.trim().toLowerCase());
                            if (existing) {
                              const updated = [...vendorProducts];
                              updated[idx].product_id = String(existing.id);
                              updated[idx].isNewProduct = false;
                              updated[idx].newProductName = '';
                              setVendorProducts(updated);
                              return;
                            }
                            
                            try {
                              const newProd = await vmsService.createProduct({ name: prod.newProductName.trim() });
                              const data = await vmsService.getProducts();
                              setProductsList(data.results || data);
                              
                              const updated = [...vendorProducts];
                              updated[idx].product_id = String(newProd.id);
                              updated[idx].isNewProduct = false;
                              updated[idx].newProductName = '';
                              setVendorProducts(updated);
                            } catch (err) {
                              alert('Failed to save new product');
                            }
                          }} 
                          style={{ fontSize: '0.85rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="filter-group" style={{ flex: '1 1 200px' }}>
                  <label>Description</label>
                  <input type="text" value={prod.description} onChange={e => {
                    const updated = [...vendorProducts];
                    updated[idx].description = e.target.value;
                    setVendorProducts(updated);
                  }} placeholder="Details..." />
                </div>
                <div className="filter-group" style={{ flex: '1 1 120px' }}>
                  <label>Quoted Price *</label>
                  <input type="number" step="0.01" value={prod.quoted_price} onChange={e => {
                    const updated = [...vendorProducts];
                    updated[idx].quoted_price = e.target.value;
                    setVendorProducts(updated);
                  }} required placeholder="e.g. 50000" />
                </div>
                <div className="filter-group" style={{ flex: '1 1 100px' }}>
                  <label>Quantity *</label>
                  <input type="number" value={prod.quantity} onChange={e => {
                    const updated = [...vendorProducts];
                    updated[idx].quantity = e.target.value;
                    setVendorProducts(updated);
                  }} required min="1" placeholder="e.g. 10" />
                </div>
                <button 
                  type="button" 
                  onClick={() => handleRemoveProduct(idx)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', marginTop: '36px', marginLeft: 'auto', color: '#ef4444' }}
                  title="Remove Product"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
            ))}
            <button 
              type="button" 
              className="secondary-btn" 
              onClick={() => setVendorProducts(prev => [...prev, { product_id: '', product_name: '', isNewProduct: false, newProductName: '', description: '', quoted_price: '', quantity: '1' }])}
              style={{ alignSelf: 'flex-start' }}
            >
              + Add Product
                </button>
              </div>
          </>

          <div className="panel-actions">
            <button type="button" className="secondary-btn" onClick={() => navigate('/vms')} disabled={isLoading}>Cancel</button>
            <button type="submit" className="primary-btn" disabled={isLoading}>
              {isLoading ? 'Saving...' : (initialData ? 'Save Changes' : 'Save Vendor')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
